import React, { useState, useEffect } from 'react';
import { makeStyles, Typography, Grid, MenuItem, FormControl, Button, TextField, Select, Paper, TableContainer, Table, TableBody, TableCell, TableRow, TablePagination, TableHead, TableSortLabel } from '@material-ui/core';
import usePermissions from 'hooks/permissions-hooks';
import SearchIcon from '@material-ui/icons/Search';
import CollectionKeys from 'hooks/molecules/collection-keys';
import AppLoader from 'components/basic/AppLoader';
import { useItems } from 'hooks/atoms/items-hook';
import { getFullRegistrationStatus, parseDate } from 'components/business/consents/ConsentsTable';
import ManagersSampleReceivedForm from 'components/business/local-manager/ManagersSampleReceivedForm';
import { roles } from '../cassandra-shared-js/roles';

const useStyles = makeStyles({
  title: {
    margin: '0px 0px 5px 5px',
    color: 'rgb(100, 141, 174)',
    fontSize: 35,
  },
  subtitle: {
    margin: '0px 0px 5px 5px',
    color: 'rgb(100, 141, 174)',
    fontSize: 18,
  },
  button: {
    margin: '6px 15px 0px 0px',
    fontSize: 18,
    borderColor: 'rgb(100, 141, 174)',
    color: '#f0f4f8',
    backgroundColor: 'rgb(100, 141, 174)',
    '&:hover': {
      backgroundColor: 'rgb(41, 58, 136)',
    },
  },
  selectLocation: {
    fontSize: 18,
    color: 'rgb(100, 141, 174)',
    margin: '0px 0px 20px 5px',
  },
  searchBox: {
    fontSize: 18,
    color: 'rgb(100, 141, 174)',
    margin: '6px 0px 0px 5px',
  },
  tableContainer: {
    margin: 'auto',
    borderRadius: 8,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  },
  table: {
    minWidth: 650,
  },
  headerCell: {
    backgroundColor: 'rgb(100, 141, 174)',
    color: '#f0f4f8',
    fontWeight: 600,
    fontSize: 16,
  },
  row: {
    '&:nth-of-type(odd)': {
      backgroundColor: '#fafafa',
    },
    '&:hover': {
      backgroundColor: '#f0f0f0',
    },
  },
});

function ManagerSampleReceivedPage() {
  const classes = useStyles();
  const { userHasRole, role } = usePermissions();
  const [ filterQuery, setFilterQuery ] = useState({});
  const [ country, setCountry ] = useState('GR');
  const [ searchSerial, setSearchSerial ] = useState('');
  const [ thematic, setThematic ] = useState('All');

  // Select Country
  useEffect(() => {
    if (userHasRole(roles.AL_MANAGER)) {
      setCountry('AL');
    } else if (userHasRole(roles.ADMINISTRATOR_ROLE)) {
      setCountry('GR');
    }
    refetch();
  }, [ role ]);

  const handleThematic = (value) => {
    setThematic(value);
    if (value !== 'All') {
      setFilterQuery({ ...filterQuery, 'thematic_package.real_name_eq': value });
    } else {
      delete filterQuery['thematic_package.real_name_eq'];
      setFilterQuery(filterQuery);
    }
    refetch();
  };

  const {
    countFiltered,
    items: kits,
    itemsLoading: kitsLoading,
    refetch,
    itemsFetching,
    itemsError: kitsError,
    pagination,
  } = useItems(CollectionKeys.REGISTRATION_KITS, { filterQuery: {
    ...filterQuery,
    _where: {
      'serial_number_id.country_eq': country,
      'serial_number_id.status_eq': 'correlated',
    },
  },
  queryOptions: { refetchOnWindowFocus: false } });

  const handleSearch = () => {
    const newFilter = { 'serial_number_id.serial_contains': searchSerial };
    setFilterQuery({ ...filterQuery, ...newFilter });
    refetch();
  };

  // Start search with Enter key
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = pagination.sort.orderBy === property && pagination.sort.order === 'asc';
    pagination.setSort(property, isAsc ? 'desc' : 'asc');
    pagination.setPage(0);
  };

  if (kitsError) {
    return (
      <span>
        Error:
        {kitsError}
      </span>
    );
  }

  return (
    <>
      <Grid container spacing={ 2 } alignItems='center' style={ { backgroundColor: '#ffffff' } }>
        <Grid item xs={ 12 }>
          <div className={ classes.title }>
            Sample Received
          </div>
          <div className={ classes.subtitle }>
            Entries:
            {' '}
            {countFiltered}
          </div>
        </Grid>
        {userHasRole(roles.ADMINISTRATOR_ROLE) && (
          <Grid item xs={ 6 } md={ 2 } display='flex'>
            <FormControl style={ { width: '100%' } } className={ classes.selectLocation }>
              <Typography
                variant='subtitle'
                id='location-select-label'
              >
                Location:
              </Typography>
              <Select
                variant='outlined'
                size='small'
                labelId='country-select-label'
                id='country-select'
                data-testid='country-select-id'
                value={ country }
                onChange={ (e) => { setCountry(e.target.value); } }
              >
                <MenuItem value='GR'>Greece</MenuItem>
                <MenuItem value='AL'>Albania</MenuItem>
                <MenuItem value='IN'>India</MenuItem>
                <MenuItem value='IQ'>Iraq</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid item xs={ 6 } md={ 2 } display='flex'>
          <FormControl style={ { width: '100%' } } className={ classes.selectLocation }>
            <Typography
              variant='subtitle'
              id='thematic-select-label'
            >
              Thematic Package:
            </Typography>
            <Select
              variant='outlined'
              size='small'
              labelId='thematic-select-label'
              id='thematic-select'
              value={ thematic }
              onChange={ (e) => {
                handleThematic(e.target.value);
              } }
            >
              <MenuItem value='All'>All</MenuItem>
              <MenuItem value='iDNA PGx CNS Kit'>iDNA PGx kit</MenuItem>
              {country === 'GR' && <MenuItem value='iDNA Test Vitamins & Minerals'>Vitamins kit</MenuItem>}
              <MenuItem value='iDNA Cardio Health'>iDNA Cardio Health kit</MenuItem>
              <MenuItem value='NutriGenetix Laboratories kit'>NutriGenetix Laboratories kit</MenuItem>
              {country === 'GR' && <MenuItem value='iDNA NutriGenetix kit'>NutriGenetix kit</MenuItem>}
              {country === 'GR' && <MenuItem value='iDNA Nutrition Test'>Nutritional kit</MenuItem>}
              {country === 'GR' && <MenuItem value='iDNA Test 3in1'>3-in-1 kit</MenuItem>}
              {country === 'GR' && <MenuItem value='iDNA Biological Age'>Biological Age kit</MenuItem>}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={ 6 } md={ 2 } display='flex'>
          <TextField
            className={ classes.searchBox }
            id='search-bar'
            size='medium'
            value={ searchSerial }
            onChange={ (e) => { setSearchSerial(e.target.value); } }
            onKeyPress={ (e) => { handleKeyPress(e); } }
            label='Enter Serial Number'
            variant='outlined'
            defaultValue='20px Text'
          />
        </Grid>
        <Grid item xs={ 6 } display='flex'>
          <Button
            className={ classes.button }
            variant='outlined'
            size='large'
            onClick={ () => { handleSearch(); } }
          >
            <SearchIcon />
            SEARCH
          </Button>
          { searchSerial.length > 0
      && (
        <Button
          className={ classes.button }
          variant='outlined'
          size='large'
          onClick={ () => {
            setSearchSerial('');
            setFilterQuery({});
          } }
        >
          CLEAR
        </Button>
      )}
        </Grid>
      </Grid>
      <Grid item xs={ 12 } md={ 12 }>
        <Paper>
          <TableContainer className={ classes.tableContainer }>
            { (kitsLoading || itemsFetching) ? <AppLoader />
              : (
                <Table className={ classes.table }>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        style={ { width: 200 } }
                        className={ classes.headerCell }
                        sortDirection={ pagination.sort.orderBy === 'serial_number_id.serial' ? pagination.sort.order : false }
                      >
                        <TableSortLabel
                          active={ pagination.sort.orderBy === 'serial_number_id.serial' }
                          direction={ pagination.sort.orderBy === 'serial_number_id.serial' ? pagination.sort.order : 'asc' }
                          onClick={ () => { return handleRequestSort('serial_number_id.serial'); } }
                        >
                          Serial Number
                        </TableSortLabel>
                      </TableCell>
                      <TableCell className={ classes.headerCell }>
                        Registration Status
                      </TableCell>
                      <TableCell className={ classes.headerCell }>
                        Sample Received Date
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {kits.map((kit) => {
                      return (
                        <TableRow key={ kit.id } className={ classes.row }>
                          <TableCell style={ { width: 200 } }>{kit?.serial_number_id?.serial}</TableCell>
                          <TableCell>{getFullRegistrationStatus(kit)}</TableCell>
                          <TableCell style={ { width: 500 } }>
                            {
                              kit?.metadata?.sampleReceivedDate !== undefined ? parseDate(kit?.metadata?.sampleReceivedDate)
                                : (
                                  <ManagersSampleReceivedForm
                                    kit={ kit }
                                    country={ country }
                                    refetch={ refetch }
                                  />
                                )
                            }
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
          </TableContainer>

          <TablePagination
            component='div'
            count={ countFiltered }
            page={ pagination.page }
            onChangePage={ (e, p) => {
              pagination.setPage(p);
            } }
            rowsPerPage={ pagination.limit }
            onChangeRowsPerPage={
              (e) => {
                const newLimit = parseInt(e.target.value, 10);
                pagination.setLimit(newLimit);
                pagination.setPage(0);
              }
            }
            rowsPerPageOptions={ [ 5, 10, 25 ] }
          />
        </Paper>

      </Grid>
    </>
  );
}

export default ManagerSampleReceivedPage;
